import React, { useState } from 'react';
import { faCalendarDays } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { formatDateUTC } from 'helpers/dateParser';
import {
  isValidReportingPeriod,
  partitionTemplateIssues,
  renderTemplateIssue,
  toUtcMidnightIso,
} from './templateIssues';

const inputClass =
  'block w-full rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-800 ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 disabled:bg-slate-100';

/**
 * Asks for the reporting period of a template that does not state a readable
 * one. It is the one fault in a template the user can fix without reopening
 * Excel, and the period entered applies to every row of the file.
 *
 * - `parsedData` — the parse result; the prompt shows while it still carries a
 *   period issue, and names the cells the period was expected in.
 * - `period` — the period already entered (`{ from, to }`), once there is one.
 * - `onApply({ from, to })` — ISO datetimes at UTC midnight. The caller applies
 *   them with `applyReportingPeriod`.
 * - `onChange()` — the user wants to enter a different period.
 *
 * Renders nothing when the template states its own period and none was entered.
 */
const ReportingPeriodPrompt = ({
  parsedData,
  period,
  onApply,
  onChange,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation('common');
  const { periodIssues } = partitionTemplateIssues(parsedData);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  if (period && periodIssues.length === 0) {
    return (
      <div
        className={`bg-sky-50 border border-sky-200 rounded-lg px-4 py-3 flex items-center gap-3 text-sm ${className}`}
        data-testid="reporting-period-entered"
      >
        <FontAwesomeIcon
          icon={faCalendarDays}
          className="text-sky-500"
        />
        <span className="text-sky-800 flex-1">
          {t('templateIssues.period.entered', 'Reporting period entered: {{from}} – {{to}}', {
            from: formatDateUTC(period.from, 'MMM D, YYYY'),
            to: formatDateUTC(period.to, 'MMM D, YYYY'),
          })}
        </span>
        {onChange && (
          <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className="text-sky-700 font-medium hover:underline disabled:opacity-50"
          >
            {t('templateIssues.period.change', 'Change')}
          </button>
        )}
      </div>
    );
  }

  if (periodIssues.length === 0) return null;

  const reversed = Boolean(from && to && from > to);
  const candidate = { from, to };

  return (
    <div
      className={`bg-sky-50 border border-sky-200 rounded-lg p-4 flex items-start gap-3 ${className}`}
      data-testid="reporting-period-prompt"
    >
      <FontAwesomeIcon
        icon={faCalendarDays}
        className="text-sky-500 mt-0.5 shrink-0"
      />
      <div className="flex-1 text-sm">
        <p className="font-medium text-sky-900">
          {t('templateIssues.period.title', 'Enter the reporting period')}
        </p>
        <p className="text-sky-800 mt-0.5">{renderTemplateIssue(periodIssues[0])}</p>
        <p className="text-sky-800 mt-0.5">
          {t(
            'templateIssues.period.hint',
            'The period you enter applies to every row of this file.',
          )}
        </p>
        <div className="flex flex-wrap items-end gap-3 mt-3">
          <label className="text-xs font-medium text-sky-900">
            {t('templateIssues.period.from', 'Start')}
            <input
              type="date"
              value={from}
              max={to || undefined}
              onChange={(event) => setFrom(event.target.value)}
              disabled={disabled}
              className={`${inputClass} mt-1`}
            />
          </label>
          <label className="text-xs font-medium text-sky-900">
            {t('templateIssues.period.to', 'End')}
            <input
              type="date"
              value={to}
              min={from || undefined}
              onChange={(event) => setTo(event.target.value)}
              disabled={disabled}
              className={`${inputClass} mt-1`}
            />
          </label>
          <CgRadixButton
            variant="primary"
            size="sm"
            disabled={disabled || !isValidReportingPeriod(candidate)}
            onClick={() => onApply({ from: toUtcMidnightIso(from), to: toUtcMidnightIso(to) })}
          >
            {t('templateIssues.period.apply', 'Apply period')}
          </CgRadixButton>
        </div>
        {reversed && (
          <p
            role="alert"
            className="text-red-600 mt-2"
          >
            {t('templateIssues.period.reversed', 'The start date must not be after the end date.')}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReportingPeriodPrompt;
